class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.boundClose = this.close.bind(this);

    document.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.boundClose);
    this.setHeaderCartIconAccessibility();
    this.overlay = document.createElement('div');
    document.body.append(this.overlay);
    this.overlay.style.height = document.body.offsetHeight + 'px';

    // Use event delegation for close buttons to handle dynamic content
    this.addEventListener('click', (event) => {
      if (event.target.closest('.drawer__close')) {
        this.close();
      }
    });

    // Listen for cart update events to re-initialize Okendo widget
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (this.classList.contains('active')) {
        setTimeout(() => {
          const widgetElement = document.querySelector('.review-cart-widget div[data-oke-badge]');
          if (widgetElement && window.okeWidgetApi) {
            window.okeWidgetApi.initWidget(widgetElement);
          }
        }, 100);
      }
    });
  }

  onOutsideClick(e) {
    if (window.__dropdownIsClosing) return;

    if (!this.contains(e.target) && this.classList.contains('active')) {
      this.close();
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      if (!this.classList.contains('active')) {
        this.open(cartLink);
      } else {
        this.close();
      }
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('aria-controls')) this.setSummaryAccessibility(cartDrawerNote);

    if (window.updateWishlistStateImmediately) {
      window.updateWishlistStateImmediately(this);
    }

    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });
    this.overlay.classList.add('overlay-drawer--open');
    document.body.classList.add('overflow-hidden', 'cart-drawer-opened');
  }

  close() {
    this.classList.remove('active');
    this.overlay.classList.remove('overlay-drawer--open');
    document.body.classList.remove('overflow-hidden', 'cart-drawer-opened');
  }

  setSummaryAccessibility(cartDrawerNote) {
    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState, isCartReco = false) {
    const isTransitioningFromEmpty =
      this.querySelector('.drawer__inner').classList.contains('is-empty') ||
      !this.querySelector('#CartDrawer-CartItems') ||
      this.querySelector('#CartDrawer-CartItems').querySelector('.drawer__cart-items-wrapper') === null;

    const recoCount = this.querySelectorAll('#Slider-reco-minicart .slider__slide').length;
    const isLowReco = isCartReco && recoCount < 4;

    if (isTransitioningFromEmpty || isLowReco) {
      if (this.classList.contains('is-empty')) this.classList.remove('is-empty');
      if (this.querySelector('.drawer__inner').classList.contains('is-empty')) {
        this.querySelector('.drawer__inner').classList.remove('is-empty');
      }
    }

    this.productId = parsedState.id;

    if (isCartReco && !isTransitioningFromEmpty && !isLowReco) {
      this.getDrawerLightRenderSelectors().forEach((selector) => {
        const targetElement = this.querySelector(selector);
        if (targetElement) {
          targetElement.innerHTML = this.getSectionInnerHTML(parsedState.sections['cart-drawer'], selector);
        }
      });

      // Also update the header cart icon bubble
      const headerSection = this.getSectionsToRender().find((s) => s.selector === '#cart-icon-bubble');
      if (headerSection && parsedState.sections[headerSection.id]) {
        const headerElement = document.querySelector(headerSection.selector);
        if (headerElement) {
          headerElement.innerHTML = this.getSectionInnerHTML(
            parsedState.sections[headerSection.id],
            headerSection.selector,
          );
        }
      }
    } else {
      this.getSectionsToRender().forEach((section) => {
        const sectionElement = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);
        if (sectionElement && parsedState.sections[section.id]) {
          sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
        }
      });
    }

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.boundClose);
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: document.querySelector('[id$=header]')?.id.split('shopify-section-')[1] || 'header',
        selector: '#cart-icon-bubble',
      },
    ];
  }

  getDrawerLightRenderSelectors() {
    return [
      '.drawer__header',
      '.cart__free-shipping-progress',
      '.cart-drawer__hold-in-bag',
      '.drawer__contents.js-contents',
      '.cart-drawer__totals',
      '.extra-services--minicart',
      '.cart-drawer-discount-input',
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  reattachEventListeners() {
    // Re-attach overlay event listener
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) {
      overlay.removeEventListener('click', this.boundClose);
      overlay.addEventListener('click', this.boundClose);
    }
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  constructor() {
    super();
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: this.minicartSelector,
      },
      {
        id: 'cart-drawer-wrapper',
        section: 'header',
        selector: '#cart-icon-bubble',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
